"use client"

import { notify } from "@/lib/notifications/notify"
import { useState } from "react"
import {
  X,
  Check,
  XIcon,
  MessageSquare,
  User,
  Calendar,
  Mail,
  Phone,
  Clock,
  Sparkles,
  ArrowRight,
  Building2,
  AlertCircle,
} from "lucide-react"
import { Button } from "@/components/ui/button"
import { createBrowserClient } from "@/lib/supabase/client"
import { useRouter } from "next/navigation"
import { useToast } from "@/hooks/use-toast"

type ReservationRequest = {
  id: string
  barber_id: string
  shop_id: string
  barber: {
    full_name: string
    email: string
    phone?: string
    avatar_url?: string
  }
  shop: {
    id: string
    name: string
  }
  rental_type: string
  requested_days: number[]
  start_date: string
  message: string
  status: string
  created_at: string
}

type Props = {
  request: ReservationRequest
  isOpen: boolean
  onClose: () => void
  onAction: (requestId: string, action: "approved" | "rejected") => void
}

export function ReservationReviewModal({ request, isOpen, onClose, onAction }: Props) {
  const [step, setStep] = useState<"review" | "onboarding" | "success">("review")
  const [isLoading, setIsLoading] = useState(false)
  const [boothNumber, setBoothNumber] = useState("")
  const [error, setError] = useState<string | null>(null)
  const router = useRouter()
  const { toast } = useToast()

  const dayNames = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"]

  async function handleApprove() {
    setIsLoading(true)
    setError(null)
    const supabase = createBrowserClient()

    // Update reservation status
    const { error: updateError } = await supabase
      .from("reservation_requests")
      .update({ status: "approved", updated_at: new Date().toISOString() })
      .eq("id", request.id)

    if (updateError) {
      setError("Failed to approve request. Please try again.")
      toast({
        title: "Error",
        description: "Failed to approve request. Please try again.",
        variant: "destructive",
      })
      setIsLoading(false)
      return
    }

    // Move to onboarding step
    setIsLoading(false)
    setStep("onboarding")
  }

  async function handleOnboardBarber() {
    setIsLoading(true)
    setError(null)
    const supabase = createBrowserClient()

    try {
      const { data: existingBarber } = await supabase
        .from("shop_barbers")
        .select("id, status")
        .eq("shop_id", request.shop.id)
        .eq("barber_id", request.barber_id)
        .maybeSingle()

      if (existingBarber) {
        // Update the existing record — but NEVER downgrade someone who is
        // already on the floor. An invited barber is 'active' from the moment
        // they accept, so approving their booth request used to knock them
        // back to 'pending_payment': off their own shops list and into "set up
        // rent", for doing the thing the owner just approved. Being in the
        // shop does not depend on payment; the booth_rentals row below is
        // what tracks the money.
        const { error: updateError } = await supabase
          .from("shop_barbers")
          .update({
            booth_number: boothNumber || null,
            status: "active",
            joined_at: existingBarber.status === "active" ? undefined : new Date().toISOString(),
          })
          .eq("id", existingBarber.id)

        if (updateError) {
          throw updateError
        }
      } else {
        // The owner approved the request — that is the same consent an invite
        // carries, so the barber joins 'active' and pays after.
        const { error: insertError } = await supabase.from("shop_barbers").insert({
          shop_id: request.shop.id,
          barber_id: request.barber_id,
          booth_number: boothNumber || null,
          status: "active",
          joined_at: new Date().toISOString(),
        })

        if (insertError) {
          throw insertError
        }
      }

      const { data: existingRental } = await supabase
        .from("booth_rentals")
        .select("id")
        .eq("shop_id", request.shop.id)
        .eq("barber_id", request.barber_id)
        .eq("status", "pending_payment")
        .maybeSingle()

      if (!existingRental) {
        // Create booth rental record with pending status
        await supabase.from("booth_rentals").insert({
          shop_id: request.shop.id,
          barber_id: request.barber_id,
          rental_type: request.rental_type,
          start_date: request.start_date,
          booked_days: request.requested_days,
          status: "pending_payment",
        })
      }

      // Send notification to barber to select plan and pay
      const { error: notifError } = await notify({
                                      recipient: request.barber_id,
                                      type: "booth_approved",
                                      shopId: request.shop.id,
                                      metadata: {
          shop_name: request.shop.name,
          shop_id: request.shop.id,
          booth_number: boothNumber,
          rental_type: request.rental_type,
        },
                                    })

      if (notifError) {
        // Non-critical, continue anyway
        console.warn("Failed to send notification:", notifError)
      }

      setIsLoading(false)
      setStep("success")

      onAction(request.id, "approved")

      if (typeof window !== "undefined") {
        window.dispatchEvent(
          new CustomEvent("reservation-approved", {
            detail: { requestId: request.id, barberId: request.barber_id, shopId: request.shop.id },
          }),
        )
      }

      toast({
        title: "Success!",
        description: `${request.barber?.full_name} has been added to your roster and notified to complete payment.`,
      })

      setTimeout(() => {
        handleClose()
      }, 2000)
    } catch (err: any) {
      setError(err.message || "Failed to onboard barber. Please try again.")
      toast({
        title: "Error",
        description: err.message || "Failed to onboard barber. Please try again.",
        variant: "destructive",
      })
      setIsLoading(false)
    }
  }

  async function handleReject() {
    setIsLoading(true)
    setError(null)
    const supabase = createBrowserClient()

    const { error: rejectError } = await supabase
      .from("reservation_requests")
      .update({ status: "rejected", updated_at: new Date().toISOString() })
      .eq("id", request.id)

    if (!rejectError) {
      await notify({
        recipient: request.barber_id,
        type: "booth_rejected",
        metadata: {
          reservation_id: request.id,
          shop_name: request.shop.name,
        },
      })

      onAction(request.id, "rejected")
      onClose()
    } else {
      setError("Failed to reject request. Please try again.")
    }
    setIsLoading(false)
  }

  function handleMessage() {
    onClose()
    router.push("/dashboard/messages")
  }

  function handleClose() {
    setStep("review")
    setBoothNumber("")
    setError(null)
    onClose()
  }

  if (!isOpen) return null

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/70 z-50" onClick={handleClose} />

      {/* Modal */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div className="bg-background rounded-3xl w-full max-w-lg max-h-[90vh] overflow-hidden shadow-2xl">
          {/* Review Step */}
          {step === "review" && (
            <>
              <div className="relative p-6 pb-4 border-b border-border">
                <button
                  onClick={handleClose}
                  className="absolute top-4 right-4 p-2 hover:bg-muted rounded-full transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
                <h2 className="text-xl font-semibold">Review Request</h2>
                <p className="text-sm text-muted-foreground mt-1">Booth rental request for {request.shop.name}</p>
              </div>

              <div className="p-6 overflow-y-auto max-h-[60vh]">
                {/* Error Display */}
                {error && (
                  <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-xl flex items-center gap-2 text-red-700 text-sm">
                    <AlertCircle className="w-4 h-4 flex-shrink-0" />
                    {error}
                  </div>
                )}

                {/* Barber Info */}
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center overflow-hidden">
                    {request.barber?.avatar_url ? (
                      <img
                        src={request.barber.avatar_url || "/placeholder.svg"}
                        alt={request.barber.full_name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <User className="w-8 h-8 text-muted-foreground" />
                    )}
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg">{request.barber?.full_name || "Unknown Barber"}</h3>
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Mail className="w-3.5 h-3.5" />
                      {request.barber?.email}
                    </div>
                    {request.barber?.phone && (
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Phone className="w-3.5 h-3.5" />
                        {request.barber.phone}
                      </div>
                    )}
                  </div>
                </div>

                {/* Request Details */}
                <div className="space-y-4">
                  <div className="bg-muted/50 rounded-[2.5rem] p-4 space-y-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Building2 className="w-4 h-4" />
                        Rental Type
                      </div>
                      <span className="text-sm font-medium capitalize">{request.rental_type}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Calendar className="w-4 h-4" />
                        Start Date
                      </div>
                      <span className="text-sm font-medium">
                        {new Date(request.start_date).toLocaleDateString("en-US", {
                          month: "long",
                          day: "numeric",
                          year: "numeric",
                        })}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Clock className="w-4 h-4" />
                        Requested
                      </div>
                      <span className="text-sm font-medium">
                        {new Date(request.created_at).toLocaleDateString("en-US", {
                          month: "short",
                          day: "numeric",
                        })}
                      </span>
                    </div>
                  </div>

                  {request.requested_days && request.requested_days.length > 0 && (
                    <div>
                      <p className="text-sm text-muted-foreground mb-2">Requested Days</p>
                      <div className="flex flex-wrap gap-2">
                        {request.requested_days.map((day) => (
                          <span
                            key={day}
                            className="px-3 py-1.5 bg-primary/10 text-primary text-sm rounded-full font-medium"
                          >
                            {dayNames[day]}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {request.message && (
                    <div>
                      <p className="text-sm text-muted-foreground mb-2">Message from Barber</p>
                      <div className="bg-muted/50 rounded-xl p-4">
                        <p className="text-sm text-foreground">{request.message}</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className="p-6 pt-4 border-t border-border">
                <div className="grid grid-cols-3 gap-3">
                  <Button
                    onClick={handleApprove}
                    disabled={isLoading}
                    className="bg-green-600 hover:bg-green-700 text-white"
                  >
                    {isLoading ? (
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <>
                        <Check className="w-4 h-4 mr-1.5" />
                        Approve
                      </>
                    )}
                  </Button>
                  <Button
                    onClick={handleReject}
                    disabled={isLoading}
                    variant="outline"
                    className="border-red-200 text-red-600 hover:bg-red-50 hover:border-red-300 bg-transparent"
                  >
                    <XIcon className="w-4 h-4 mr-1.5" />
                    Reject
                  </Button>
                  <Button onClick={handleMessage} variant="outline">
                    <MessageSquare className="w-4 h-4 mr-1.5" />
                    Message
                  </Button>
                </div>
              </div>
            </>
          )}

          {/* Onboarding Step */}
          {step === "onboarding" && (
            <>
              <div className="relative p-6 pb-4 border-b border-border">
                <button
                  onClick={handleClose}
                  className="absolute top-4 right-4 p-2 hover:bg-muted rounded-full transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
                <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center mb-4">
                  <Check className="w-6 h-6 text-green-600" />
                </div>
                <h2 className="text-xl font-semibold">Request Approved!</h2>
                <p className="text-sm text-muted-foreground mt-1">
                  Now let's onboard {request.barber?.full_name?.split(" ")[0]} to your shop
                </p>
              </div>

              <div className="p-6">
                {/* Error Display */}
                {error && (
                  <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-xl flex items-center gap-2 text-red-700 text-sm">
                    <AlertCircle className="w-4 h-4 flex-shrink-0" />
                    {error}
                  </div>
                )}

                <div className="space-y-4">
                  <div>
                    <label className="text-sm font-medium text-foreground mb-2 block">
                      Assign Booth Number (Optional)
                    </label>
                    <input
                      type="text"
                      placeholder="e.g., 1, A, Station 3"
                      value={boothNumber}
                      onChange={(e) => setBoothNumber(e.target.value)}
                      className="w-full px-4 py-3 bg-muted border border-border rounded-xl text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                    />
                    <p className="text-xs text-muted-foreground mt-2">
                      This helps organize your shop and lets the barber know their station
                    </p>
                  </div>

                  <div className="bg-muted/50 rounded-xl p-4">
                    <h4 className="font-medium text-sm mb-2">What happens next:</h4>
                    <ul className="space-y-2 text-sm text-muted-foreground">
                      <li className="flex items-start gap-2">
                        <Check className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                        Barber receives notification to select plan
                      </li>
                      <li className="flex items-start gap-2">
                        <Check className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                        Barber chooses plan and completes payment
                      </li>
                      <li className="flex items-start gap-2">
                        <Check className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                        Barber earns reward points on payment
                      </li>
                      <li className="flex items-start gap-2">
                        <Check className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                        Booth is activated and ready to use
                      </li>
                    </ul>
                  </div>
                </div>
              </div>

              <div className="p-6 pt-4 border-t border-border">
                <Button
                  onClick={handleOnboardBarber}
                  disabled={isLoading}
                  className="w-full bg-green-600 hover:bg-green-700 text-white h-12 text-base font-medium"
                >
                  {isLoading ? (
                    <div className="flex items-center gap-2">
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      <span>Sending...</span>
                    </div>
                  ) : (
                    <>
                      <Sparkles className="w-5 h-5 mr-2" />
                      Send to Barber
                      <ArrowRight className="w-5 h-5 ml-2" />
                    </>
                  )}
                </Button>
                <button
                  onClick={handleClose}
                  disabled={isLoading}
                  className="w-full mt-3 text-sm text-muted-foreground hover:text-foreground transition-colors disabled:opacity-50"
                >
                  I'll do this later
                </button>
              </div>
            </>
          )}

          {/* Success Step */}
          {step === "success" && (
            <div className="p-8 text-center">
              <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-6">
                <Sparkles className="w-10 h-10 text-green-600" />
              </div>
              <h2 className="text-2xl font-semibold mb-2">Notification Sent!</h2>
              <p className="text-muted-foreground mb-6">
                {request.barber?.full_name} will receive a notification to select their plan and complete payment.
              </p>
              <Button onClick={handleClose} className="bg-green-600 hover:bg-green-700 text-white px-8">
                Done
              </Button>
            </div>
          )}
        </div>
      </div>
    </>
  )
}
